import { useState, useRef, useEffect } from 'react';
import { PaperPlaneRight, CircleNotch, Microphone, Stop, Globe } from '@phosphor-icons/react';
import { useVoiceInput } from './useVoiceInput';

export const ChatInput = ({ onSend, disabled, webSearch, onToggleWebSearch }) => {
  const [message, setMessage] = useState('');
  const inputRef = useRef(null);
  const { listening, transcript, supported, start, stop, reset } = useVoiceInput();

  useEffect(() => {
    if (transcript) setMessage(transcript);
  }, [transcript]);

  useEffect(() => {
    if (!listening && transcript.trim()) {
      const timer = setTimeout(() => {
        onSend(transcript.trim());
        setMessage('');
        reset();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [listening, transcript, onSend, reset]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const text = message.trim();
    if (!text || disabled) return;
    onSend(text);
    setMessage('');
    reset();
    inputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const toggleVoice = () => {
    if (listening) { stop(); } else { reset(); start(); }
  };

  return (
    <div className="border-t border-[#f2f4f6] bg-white" data-testid="chat-input-area">
      {/* Toggle bar */}
      <div className="flex items-center gap-3 px-3 sm:px-4 pt-2.5 pb-1">
        <button
          type="button"
          onClick={onToggleWebSearch}
          className={`flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border transition-all ${
            webSearch
              ? 'bg-[#0f172a] text-white border-[#0f172a]'
              : 'bg-white text-[#596065] border-[#e2e8f0] hover:border-[#acb3b8]'
          }`}
          data-testid="web-search-toggle"
        >
          <Globe size={12} weight={webSearch ? 'fill' : 'bold'} />
          Web Search
        </button>
      </div>

      {/* Input row */}
      <form onSubmit={handleSubmit} className="flex items-end gap-2 px-3 sm:px-4 pb-3 pt-1">
        <textarea
          ref={inputRef}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={listening ? 'Listening...' : webSearch ? 'Search the web or ask about your tasks...' : 'Ask about your tasks, bills, or emails...'}
          disabled={disabled || listening}
          rows={1}
          className={`flex-1 resize-none rounded-xl border px-4 py-3 text-sm outline-none transition-colors placeholder:text-[#acb3b8] disabled:opacity-50 ${
            listening
              ? 'border-[#ef4444] bg-[#fef2f2] animate-pulse'
              : 'border-[#e2e8f0] bg-[#fafbfc] focus:border-[#0f172a] focus:bg-white'
          }`}
          style={{ minHeight: '44px', maxHeight: '120px' }}
          data-testid="chat-input"
        />

        {supported && (
          <button
            type="button"
            onClick={toggleVoice}
            disabled={disabled}
            className={`flex-shrink-0 w-11 h-11 rounded-xl flex items-center justify-center transition-all disabled:opacity-30 ${
              listening
                ? 'bg-[#ef4444] text-white animate-pulse shadow-lg shadow-[#ef4444]/30'
                : 'bg-[#f2f4f6] text-[#596065] hover:bg-[#e2e8f0] hover:text-[#2d3338]'
            }`}
            data-testid="voice-input-btn"
          >
            {listening ? <Stop size={18} weight="fill" /> : <Microphone size={18} weight="bold" />}
          </button>
        )}

        <button
          type="submit"
          disabled={disabled || !message.trim() || listening}
          className="flex-shrink-0 w-11 h-11 rounded-xl bg-[#0f172a] text-white flex items-center justify-center hover:bg-[#1e293b] transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          data-testid="chat-send-btn"
        >
          {disabled ? (
            <CircleNotch size={18} weight="bold" className="animate-spin" />
          ) : (
            <PaperPlaneRight size={18} weight="fill" />
          )}
        </button>
      </form>
    </div>
  );
};
